/**
 * F17 T17.1.6 v3 — strona Akceptacje z 3 zakładkami.
 *
 * URL: `/processes/decisions/:tab` (tab ∈ pending | submitted | active).
 * Sidebar "Procesy" landuje na `/processes/decisions/pending` (Commit 7
 * dorzuca special-case w Sidebar.tsx).
 *
 * Zakładki:
 *   - **Moje czekające** (`pending`) — kroki gdzie user jest akceptantem.
 *     Endpoint: `GET /api/processes/my-pending-decisions` (Commit 2 z
 *     enrichment refactor).
 *   - **Moje zgłoszenia** (`submitted`) — instancje uruchomione przez usera.
 *     Endpoint: `GET /api/processes/my-submitted-instances` (Commit 3).
 *   - **Wszystkie aktywne** (`active`) — cross-user, gated permission
 *     `processes.decisions.view_all`. Endpoint:
 *     `GET /api/processes/instances/all` (Commit 4). 403 → tab pokazuje
 *     fallback "permissionDenied" (per Commit 6 implementacja).
 *
 * Kafelki „Moje czekające" / „Przeterminowane" / „Zespół" są widokami jednej zakładki
 * (`pending`): kliknięte z niej zmieniają tylko zakres i zachowują wybrane „Grupuj według"
 * (`?groupBy=`); z zakładek „Aktywne" / „Moje zgłoszenia" działają jak dotąd (stałe adresy).
 *
 * Drill-down: klik wiersza → `resolveTargetUrl(moduleCode, recordId)` →
 * navigate do karty rekordu (np. `/contractors/<id>`). Patrz
 * `lib/processes/module-routes.ts`. ADR-0040.
 *
 * Commit 5 dostarcza shell + routing + tab switching. Commit 6 wypełnia
 * faktyczną treść taba (ListingToolbar + table + drill-down + RTL).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Clock, Send, Users } from 'lucide-react';

import { Button } from '@/components/Button';
import { StatCard } from '@/components/ui/StatCard';
import { useCanManageProcessDefinitions } from '@/hooks/useCanManageProcessDefinitions';
import { fetchDecisionStats } from '@/lib/processes/approvals-api';
import { parsePendingGroupBy, type PendingDecisionGroupBy } from '@/lib/processes/pending-group-by';
import { cn } from '@/lib/utils';

import { ActiveInstancesTab } from './ActiveInstancesTab';
import { MyPendingDecisionsTab } from './MyPendingDecisionsTab';
import { MySubmittedInstancesTab } from './MySubmittedInstancesTab';

type ApprovalsTab = 'pending' | 'submitted' | 'active';

const VALID_TABS: ReadonlyArray<ApprovalsTab> = ['pending', 'submitted', 'active'];

// Wyróżnienie kafelka odpowiadającego bieżącej zakładce (§4.12 tokeny akcentu).
const ACTIVE_TILE_CLASS = 'border-accent-500 ring-2 ring-accent-500/30';

// Kolor tła kafla per kategoria (wzorzec SellCall) — celowo literalne kolory Tailwind,
// nie tokeny motywu (jak `CARD_ICON_COLORS` w `StatCard`) — kafelek „przeterminowane" ma
// być czerwony niezależnie od koloru akcentu tenanta.
const TILE_BG = {
  pending: 'bg-amber-50 dark:bg-amber-900/20',
  overdue: 'bg-red-50 dark:bg-red-900/20',
  team: 'bg-accent-50 dark:bg-accent-900/20',
  active: 'bg-blue-50 dark:bg-blue-900/20',
  submitted: 'bg-emerald-50 dark:bg-emerald-900/20',
} as const;

function isValidTab(tab: string | undefined): tab is ApprovalsTab {
  return tab !== undefined && (VALID_TABS as readonly string[]).includes(tab);
}

export function ApprovalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const [searchParams] = useSearchParams();
  const canOpenDefinitions = useCanManageProcessDefinitions();

  // Liczniki kafelków — osobne query, NIE blokuje renderu resztki strony.
  // Loading/error → kafelki pokazują placeholder '—' (patrz statValue poniżej).
  const statsQuery = useQuery({
    queryKey: ['processes', 'decision-stats'],
    queryFn: fetchDecisionStats,
  });

  // Walidacja URL-paramu — nieznana wartość → declarative redirect na 'pending'.
  // <Navigate> komponent (NIE `navigate()` runtime) — runtime navigate podczas
  // render rzuca warning React Router'a "should be in useEffect".
  if (!isValidTab(tabParam)) {
    return <Navigate to="/processes/decisions/pending" replace />;
  }
  const activeTab: ApprovalsTab = tabParam;

  // Query w toku/błąd → placeholder '—' na kafelkach (nigdy nie blokuje strony).
  // `activeCount === null` (brak wybranej firmy / brak uprawnienia) → też '—',
  // ale NIE „0" — patrz `DecisionStats.activeCount` w approvals-api.ts.
  const stats = statsQuery.data;
  const pendingValue: ReactNode = stats ? stats.pendingCount : '—';
  const submittedValue: ReactNode = stats ? stats.submittedCount : '—';
  const activeValue: ReactNode = stats && stats.activeCount !== null ? stats.activeCount : '—';
  const overdueValue: ReactNode = stats ? stats.overdueCount : '—';
  // Kafelek „Zespół" — w przeciwieństwie do pozostałych NIE pokazuje placeholdera '—':
  // renderuje się WYŁĄCZNIE gdy `teamCount` jest liczbą (uprawnienie „podgląd zespołu"
  // jest). Dopóki query się ładuje LUB usera nie stać na to uprawnienie, kafelek
  // zostaje niewyrenderowany — patrz `DecisionStats.teamCount` w approvals-api.ts.
  const teamCount = stats && stats.teamCount !== null ? stats.teamCount : null;
  const showTeamTile = teamCount !== null;

  // Kafelek „Przeterminowane" nawiguje na zakładkę „Moje czekające" z `?overdue=true`
  // (podzbiór pendingCount) — `MyPendingDecisionsTab` czyta ten parametr i filtruje
  // listę (deep-link). Jednokierunkowe: klik zawsze WŁĄCZA filtr; wyłączenie — klik
  // kafelka „Moje czekające" (czyści query string) albo link „Wyczyść filtr
  // przeterminowanych" w pustym stanie tabeli, gdy filtr da zero wyników (patrz
  // `clearOverdueFilter` w `MyPendingDecisionsTab.tsx` — dawny przełącznik w
  // toolbarze usunięty jako redundantny z tym kaflem). Wyróżnienie kafelka wymaga sprawdzenia
  // parametru, bo aktywna zakładka sama w sobie ('pending') jest wspólna dla obu kafelków.
  // Kafelek „Zespół" analogicznie dokłada `?scope=team` (deep-link, `MyPendingDecisionsTab`
  // czyta ten parametr żeby przełączyć źródło danych na tryb tylko-podgląd). Oba kafelki
  // wykluczają się wizualnie — sprawdzamy drugi parametr, żeby nie świeciły dwa naraz.
  // Wybrane „Grupuj według" (`?groupBy=`) przechodzi między tymi trzema kafelkami (patrz
  // `pendingTileUrl` niżej).
  const overdueActive =
    activeTab === 'pending' &&
    searchParams.get('overdue') === 'true' &&
    searchParams.get('scope') !== 'team';
  const teamActive = activeTab === 'pending' && searchParams.get('scope') === 'team';

  // „Moje czekające" / „Przeterminowane" / „Zespół" to trzy widoki TEJ SAMEJ zakładki — kafelek
  // zmienia tylko zakres, więc wybrane „Grupuj według" (`?groupBy=`) przechodzi do nowego adresu.
  // Dotyczy wyłącznie kliku z tej zakładki: z „Aktywne" / „Moje zgłoszenia" `groupBy` należy do
  // innego ekranu (inna biała lista wymiarów), więc kafelki działają tam jak dotąd (stałe adresy).
  // Przenosimy tylko wymiar ze znanej listy tej zakładki (nieznana wartość w URL zakładka i tak
  // ignoruje); żadne inne parametry nie idą dalej. Adres składa `URLSearchParams`, nie sklejanie.
  const carriedGroupBy: PendingDecisionGroupBy | null =
    activeTab === 'pending' ? parsePendingGroupBy(searchParams.get('groupBy')) : null;
  function pendingTileUrl(scopeParams: Record<string, string>): string {
    const params = new URLSearchParams(scopeParams);
    if (carriedGroupBy !== null) params.set('groupBy', carriedGroupBy);
    const query = params.toString();
    return query === '' ? '/processes/decisions/pending' : `/processes/decisions/pending?${query}`;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header — title + button "Definicje procesów". */}
      <div className="border-b border-border-primary bg-bg-primary px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">
              {t('tenant.processes.approvals.title')}
            </h1>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('tenant.processes.approvals.subtitle')}
            </p>
          </div>
          {canOpenDefinitions && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/processes')}
              data-testid="approvals-definitions-link"
            >
              {t('tenant.processes.approvals.definitionsLink')}
            </Button>
          )}
        </div>

        {/* Kafelki zamiast zakładek — klik nawiguje do tej samej trasy co dawny tab.
            „Przeterminowane" to podzbiór „Moje czekające" (ta sama trasa + `?overdue=true`),
            nie osobna zakładka — stąd wyróżnienie liczone osobno (`overdueActive`), a
            kafelek „Moje czekające" gasi swoje wyróżnienie gdy filtr przeterminowanych
            jest aktywny (żeby tylko JEDEN kafelek świecił naraz). „Zespół" mirror'uje ten
            sam wzorzec z `?scope=team` (`teamActive`) — kafelek renderuje się WYŁĄCZNIE
            gdy `showTeamTile` (uprawnienie „podgląd zespołu" jest), stąd zmienna liczba
            kolumn siatki. */}
        <div
          className={`mt-4 grid grid-cols-2 gap-3 ${showTeamTile ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}
        >
          <StatCard
            label={t('tenant.processes.approvals.tabs.pending')}
            value={pendingValue}
            icon={Clock}
            iconColor="amber"
            onClick={() => navigate(pendingTileUrl({}))}
            className={cn(
              TILE_BG.pending,
              activeTab === 'pending' && !overdueActive && !teamActive && ACTIVE_TILE_CLASS,
            )}
            aria-current={
              activeTab === 'pending' && !overdueActive && !teamActive ? 'page' : undefined
            }
            data-testid="approvals-tile-pending"
          />
          <StatCard
            label={t('tenant.processes.approvals.overdueTile')}
            value={overdueValue}
            icon={AlertTriangle}
            iconColor="red"
            onClick={() => navigate(pendingTileUrl({ overdue: 'true' }))}
            className={cn(TILE_BG.overdue, overdueActive && ACTIVE_TILE_CLASS)}
            aria-current={overdueActive ? 'page' : undefined}
            data-testid="approvals-tile-overdue"
          />
          {showTeamTile && (
            <StatCard
              label={t('tenant.processes.approvals.teamTile')}
              value={teamCount}
              icon={Users}
              iconColor="accent"
              onClick={() => navigate(pendingTileUrl({ scope: 'team' }))}
              className={cn(TILE_BG.team, teamActive && ACTIVE_TILE_CLASS)}
              aria-current={teamActive ? 'page' : undefined}
              data-testid="approvals-tile-team"
            />
          )}
          <StatCard
            label={t('tenant.processes.approvals.tabs.active')}
            value={activeValue}
            icon={Activity}
            iconColor="blue"
            onClick={() => navigate('/processes/decisions/active')}
            className={cn(TILE_BG.active, activeTab === 'active' && ACTIVE_TILE_CLASS)}
            aria-current={activeTab === 'active' ? 'page' : undefined}
            data-testid="approvals-tile-active"
          />
          <StatCard
            label={t('tenant.processes.approvals.tabs.submitted')}
            value={submittedValue}
            icon={Send}
            iconColor="emerald"
            onClick={() => navigate('/processes/decisions/submitted')}
            className={cn(TILE_BG.submitted, activeTab === 'submitted' && ACTIVE_TILE_CLASS)}
            aria-current={activeTab === 'submitted' ? 'page' : undefined}
            data-testid="approvals-tile-submitted"
          />
        </div>
      </div>

      {/* Tab content — wypełnia resztę wysokości ekranu (mirror Contractors.tsx): `min-h-0`
          + `overflow-hidden` na tym poziomie, żeby scroll robiła WŁASNA tabela zakładki
          (`fillHeight`), nie cała strona. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
        <TabPanel activeTab={activeTab} />
      </div>
    </div>
  );
}

/**
 * Tab content dispatcher — renderuje właściwy tab komponent po `activeTab`.
 * Każdy tab ma własny TanStack Query hook + tabelę (3 różne endpointy
 * z Commit 2-4 backend).
 */
function TabPanel({ activeTab }: { activeTab: ApprovalsTab }) {
  const { t } = useTranslation();
  return (
    <div
      role="region"
      aria-label={t(`tenant.processes.approvals.tabs.${activeTab}`)}
      data-testid={`approvals-tabpanel-${activeTab}`}
      className="flex h-full min-h-0 flex-col"
    >
      {activeTab === 'pending' && <MyPendingDecisionsTab />}
      {activeTab === 'submitted' && <MySubmittedInstancesTab />}
      {activeTab === 'active' && <ActiveInstancesTab />}
    </div>
  );
}
